// HR filing policy — the one place to change leave-filing rules.
//
// Vacation leave notice period by length of leave (see VL_NOTICE_PERIODS):
//
//   1–2 days            -> 3 days
//   3–4 days            -> 2 weeks
//   5–6 days            -> 1 month
//   7–10 days           -> 2 months
//   More than 10 days   -> 3 months
//
// Days and weeks count WORKING days (weekends and non-working holidays don't
// count), so "2 weeks" is 10 working days. Months are calendar months: filing on
// Sep 15 with 1 month's notice makes Oct 15 the earliest leave date.
//
// Sick / Emergency leave: no notice; notify your lead immediately and file
// afterwards (up to 2 weeks back).

import { Department, LeaveType, LeaveTypeCode } from "../models/leaveModels";

/**
 * Departments whose people get the Manager Queue. Everyone else (Technical,
 * Functional) is a regular employee and only sees their own screens.
 * Approvals are still routed by each employee's ManagerEmail, so that should
 * always point to someone in one of these departments.
 */
export const APPROVER_DEPARTMENTS: Department[] = ["Admin", "Management"];

export function isApproverDepartment(department?: string): boolean {
  return APPROVER_DEPARTMENTS.includes(department as Department);
}

/**
 * Leave types employees can pick when filing, in dropdown order. The other
 * types stay in SharePoint but are hidden for now. LWOP is never picked
 * directly — it's applied automatically when balance runs out.
 */
export const FILEABLE_LEAVE_CODES: LeaveTypeCode[] = ["VL", "SL", "EL"];

/** Largest number of days a single request can cover. */
export const MAX_DAYS_PER_REQUEST = 30;

/**
 * A medical certificate must be attached before submitting, from this many
 * days. Sick leave of 3+ days can't be filed without one.
 */
export const MED_CERT_REQUIRED_FROM_DAYS: Partial<Record<LeaveTypeCode, number>> = {
  SL: 3,
};

/** Largest attachment accepted, in megabytes. */
export const MAX_ATTACHMENT_MB = 10;

/** File types accepted for attachments (PDF or a phone photo). */
export const ATTACHMENT_ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,.heif";

/**
 * From how many days this leave type needs a medical certificate, or
 * undefined if it never does. The policy above wins; other types fall back
 * to the LeaveTypes list (RequiresMedCert / MedCertThresholdDays).
 */
export function medCertRequiredFromDays(leaveType: LeaveType): number | undefined {
  const fromPolicy = MED_CERT_REQUIRED_FROM_DAYS[leaveType.code];
  if (fromPolicy !== undefined) return fromPolicy;
  return leaveType.requiresMedCert ? Math.max(1, leaveType.medCertThresholdDays) : undefined;
}

/** "2 weeks" expressed in working days. */
export const TWO_WEEKS_WORKING_DAYS = 10;

/**
 * Pending requests whose first day is this many working days away (or fewer)
 * are flagged as urgent in the Manager Queue. The reminder flow uses the same
 * window (as ~5 calendar days) to escalate re-notifications.
 */
export const URGENT_WITHIN_WORKING_DAYS = 3;

/** A notice period: N working days, or N calendar months. */
export interface Notice {
  amount: number;
  unit: "workingDays" | "months";
}

export interface VlNoticePeriod {
  /** Longest request (in days) this row covers. */
  maxDays: number;
  /** How the row reads in the policy, e.g. "3–4 days". */
  length: string;
  notice: Notice;
  /** e.g. "2 weeks' notice". */
  noticeText: string;
}

/** Vacation leave notice periods, shortest leave first. The first row that fits applies. */
export const VL_NOTICE_PERIODS: VlNoticePeriod[] = [
  { maxDays: 2, length: "1–2 days", notice: { amount: 3, unit: "workingDays" }, noticeText: "3 working days' notice" },
  { maxDays: 4, length: "3–4 days", notice: { amount: TWO_WEEKS_WORKING_DAYS, unit: "workingDays" }, noticeText: "2 weeks' notice" },
  { maxDays: 6, length: "5–6 days", notice: { amount: 1, unit: "months" }, noticeText: "1 month's notice" },
  { maxDays: 10, length: "7–10 days", notice: { amount: 2, unit: "months" }, noticeText: "2 months' notice" },
  { maxDays: Infinity, length: "more than 10 days", notice: { amount: 3, unit: "months" }, noticeText: "3 months' notice" },
];

export interface NoticeRule {
  /** Notice required before the first leave day (amount 0 = none). */
  notice: Notice;
  /** How many working days back past dates may be filed (0 = no backdating). */
  backdateDays: number;
  /** One-line explanation shown under the leave type. */
  summary: string;
}

/** The notice rule for a leave type and number of days. */
export function noticeRuleFor(
  code: LeaveTypeCode,
  days: number,
  fallbackLeadTimeDays = 0,
): NoticeRule {
  switch (code) {
    case "VL": {
      const period =
        VL_NOTICE_PERIODS.find((p) => days <= p.maxDays) ??
        VL_NOTICE_PERIODS[VL_NOTICE_PERIODS.length - 1];
      return {
        notice: period.notice,
        backdateDays: 0,
        summary: `Vacation leave of ${period.length} needs ${period.noticeText}.`,
      };
    }
    case "SL":
    case "EL":
      return {
        notice: { amount: 0, unit: "workingDays" },
        backdateDays: TWO_WEEKS_WORKING_DAYS,
        summary:
          "Notify your lead right away. You can file on the day or up to 2 weeks after.",
      };
    default:
      return {
        notice: { amount: fallbackLeadTimeDays, unit: "workingDays" },
        backdateDays: 0,
        summary:
          fallbackLeadTimeDays > 0
            ? `File at least ${fallbackLeadTimeDays} working day(s) before.`
            : "No advance notice required.",
      };
  }
}
